<template>
  <div v-if="config.stats?.length" class="summary-grid">
    <article v-for="stat in config.stats" :key="stat.label" class="summary-card">
      <div>
        <span>{{ stat.label }}</span>
        <strong>{{ stat.value }}</strong>
      </div>
      <small :class="`is-${stat.tone ?? 'brand'}`">{{ stat.sub }}</small>
    </article>
  </div>

  <article class="content-card">
    <div v-if="config.filters?.length" class="filter-bar">
      <template v-for="filter in config.filters" :key="filter.placeholder">
        <label v-if="filter.type === 'input'" class="filter-input">
          <el-icon><Search /></el-icon>
          <input v-model.trim="pendingFilters[filter.placeholder]" type="text" :placeholder="filter.placeholder" @keydown.enter="applyFilters" />
        </label>
        <select v-else v-model="pendingFilters[filter.placeholder]" class="filter-select">
          <option v-for="option in filter.options" :key="option" :value="option">{{ option }}</option>
        </select>
      </template>
      <div class="filter-bar__actions">
        <button class="search-button" @click="applyFilters">搜索</button>
        <button class="reset-button" @click="resetFilters">重置</button>
      </div>
    </div>

    <div v-if="config.cards?.length" class="card-grid">
      <article v-for="card in config.cards" :key="card.title" class="business-card">
        <div class="business-card__head">
          <div>
            <h2>{{ card.title }}</h2>
            <small>{{ card.meta[0] }}</small>
          </div>
          <span v-if="card.status" :class="['status-tag', statusTone(card.status)]">{{ card.status }}</span>
        </div>
        <div class="business-card__body">
          <p v-for="item in card.meta.slice(1)" :key="item">{{ item }}</p>
        </div>
        <div v-if="card.footer" class="business-card__footer">
          <span>{{ card.footer }}</span>
          <div>
            <button @click="openCardAction(card, '管理')">管理</button>
            <button @click="openCardAction(card, '配置')">配置</button>
          </div>
        </div>
      </article>
    </div>

    <div v-if="config.columns?.length && filteredRows.length" class="table-wrap">
      <table>
        <thead>
          <tr>
            <th v-for="column in config.columns" :key="column.key" :style="{ width: column.width }" :class="column.align === 'right' ? 'is-right' : ''">{{ column.label }}</th>
            <th class="is-right">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in filteredRows" :key="rowIndex">
            <td v-for="column in config.columns" :key="column.key" :class="column.align === 'right' ? 'is-right' : ''">
              <span v-if="column.toneMap" :class="['status-tag', toneClass(getTone(column, row))]">{{ row[column.key] }}</span>
              <span v-else class="cell-text">{{ row[column.key] }}</span>
            </td>
            <td class="is-right">
              <div class="row-actions">
                <button title="查看详情" @click="openRowAction(row, '查看')"><el-icon><View /></el-icon></button>
                <button title="编辑" @click="openRowAction(row, '编辑')"><el-icon><EditPen /></el-icon></button>
                <button title="更多" @click="openRowAction(row, '更多')"><el-icon><More /></el-icon></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <el-empty v-else-if="config.columns?.length" description="暂无匹配数据" />
  </article>

  <el-dialog v-model="editorVisible" :title="editorTitle" width="640px">
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions">
          <GuidedField label="兑换项名称" hint="用户在小程序积分商城看到的名称。" required>
            <el-input v-model.trim="form.name" placeholder="如 饮品满20减5券" />
          </GuidedField>
          <GuidedField label="兑换类型" hint="优惠券会进入用户卡包，实物用于门店领取。">
            <el-select v-model="form.type">
              <el-option label="优惠券" value="优惠券" />
              <el-option label="实物" value="实物" />
            </el-select>
          </GuidedField>
          <GuidedField v-if="form.type === '实物'" label="实物图片" hint="小程序积分商城展示的实物主图。" wide required>
            <LocalImagePicker v-model="form.imageUrl" button-text="上传实物图片" />
          </GuidedField>
          <GuidedField label="所需积分" hint="兑换时从用户账户扣减的积分数。">
            <el-input v-model.trim="form.cost" placeholder="如 260" />
          </GuidedField>
          <GuidedField label="兑换内容" hint="优惠券面额、门店领取说明或实物描述。">
            <el-input v-model.trim="form.reward" placeholder="如 满 20 减 5" />
          </GuidedField>
          <GuidedField label="库存" hint="剩余可兑换数量，售罄后用户端不可兑换。">
            <el-input v-model.trim="form.stock" placeholder="如 3286" />
          </GuidedField>
          <GuidedField label="兑换限制" hint="同一用户可兑换次数或张数限制。">
            <el-input v-model.trim="form.limit" placeholder="如 每人 1 张" />
          </GuidedField>
          <GuidedField label="上架状态" hint="下架后小程序端不会展示或不可兑换。">
            <el-select v-model="form.status">
              <el-option label="上架" value="上架" />
              <el-option label="下架" value="下架" />
              <el-option label="售罄" value="售罄" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="editorVisible = false">{{ isViewMode ? '关闭' : '取消' }}</el-button>
      <el-button v-if="!isViewMode" type="primary" :disabled="!canSubmitFallbackForm" @click="submitFallbackForm">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRefs, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { EditPen, More, Search, View } from '@element-plus/icons-vue'
import GuidedField from '@/components/GuidedField.vue'
import LocalImagePicker from '@/components/LocalImagePicker.vue'
import type { OperationCard, OperationColumn, OperationPageConfig } from '@/data/operationDesign'

const props = defineProps<{
  config: OperationPageConfig
}>()

const { config } = toRefs(props)
const pendingFilters = reactive<Record<string, string>>({})
const activeFilters = reactive<Record<string, string>>({})
const editorVisible = ref(false)
const editorTitle = ref('配置兑换项')
const editorMode = ref<'view' | 'edit'>('edit')
const form = reactive({
  name: '',
  type: '优惠券',
  cost: '',
  reward: '',
  stock: '',
  limit: '',
  status: '上架',
  imageUrl: '',
})

watch(config, initFilters, { immediate: true })

const filteredRows = computed(() => {
  const rows = config.value.rows ?? []
  return rows.filter((row) => matchKeyword(row) && matchSelect(row, '兑换类型', 'type') && matchSelect(row, '上架状态', 'status'))
})
const isViewMode = computed(() => editorMode.value === 'view')
const canSubmitFallbackForm = computed(() => Boolean(form.name && (form.type !== '实物' || form.imageUrl)))

const toneClass = (tone?: string) => `is-${tone ?? 'muted'}`
const getTone = (column: OperationColumn, row: Record<string, string>) => column.toneMap?.[row[column.key] ?? ''] ?? 'muted'
const statusTone = (value: string) => {
  if (value.includes('营业') || value.includes('启用') || value.includes('成功') || value.includes('已配置')) return 'is-success'
  if (value.includes('休息') || value.includes('待') || value.includes('预警') || value.includes('建设')) return 'is-warning'
  if (value.includes('停用') || value.includes('失败') || value.includes('禁用')) return 'is-error'
  return 'is-brand'
}

function initFilters(): void {
  for (const filter of config.value.filters ?? []) {
    const value = filter.type === 'select' ? filter.options?.[0] ?? '' : ''
    pendingFilters[filter.placeholder] = value
    activeFilters[filter.placeholder] = value
  }
}

function applyFilters(): void {
  for (const filter of config.value.filters ?? []) {
    activeFilters[filter.placeholder] = pendingFilters[filter.placeholder] ?? ''
  }
  ElMessage.success('筛选已更新')
}

function resetFilters(): void {
  for (const filter of config.value.filters ?? []) {
    const value = filter.type === 'select' ? filter.options?.[0] ?? '' : ''
    pendingFilters[filter.placeholder] = value
    activeFilters[filter.placeholder] = value
  }
  ElMessage.success('筛选已重置')
}

function matchKeyword(row: Record<string, string>): boolean {
  const keyword = (activeFilters['搜索兑换项名称'] ?? '').trim()
  if (!keyword) return true
  return Object.values(row).some((value) => String(value).includes(keyword))
}

function matchSelect(row: Record<string, string>, placeholder: string, key: string): boolean {
  const value = activeFilters[placeholder] ?? ''
  if (!value || value.startsWith('全部')) return true
  return row[key] === value
}

function openCreateAction(): void {
  openEditor('新增兑换项', {
    name: '',
    type: '优惠券',
    cost: '',
    reward: '',
    stock: '',
    limit: '每人 1 张',
    status: '上架',
    imageUrl: '',
  })
}

function openCardAction(card: OperationCard, action: string): void {
  openEditor(`${action}${card.title}`, {
    name: card.title,
    type: card.title.includes('实物') ? '实物' : '优惠券',
    cost: '',
    reward: card.footer ?? '',
    stock: '',
    limit: '',
    status: card.status?.includes('配置') ? '上架' : '下架',
    imageUrl: '',
  })
}

function openRowAction(row: Record<string, string>, action: string): void {
  openEditor(`${action}兑换项`, row, action === '查看' ? 'view' : 'edit')
}

function openEditor(title: string, row: Partial<Record<string, string>>, mode: 'view' | 'edit' = 'edit'): void {
  editorTitle.value = title
  editorMode.value = mode
  form.name = row.name ?? ''
  form.type = row.type ?? '优惠券'
  form.cost = row.cost ?? ''
  form.reward = row.reward ?? ''
  form.stock = row.stock ?? ''
  form.limit = row.limit ?? ''
  form.status = row.status ?? '上架'
  form.imageUrl = row.imageUrl ?? ''
  editorVisible.value = true
}

function submitFallbackForm(): void {
  if (form.type === '实物' && !form.imageUrl) {
    ElMessage.warning('请先上传实物图片')
    return
  }
  editorVisible.value = false
  ElMessage.success('配置已填写，请接入后台积分商城保存接口后提交到后端')
}

defineExpose({
  openCreateAction,
})
</script>

<style scoped lang="scss">
button { border: 0; cursor: pointer; font: inherit; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card div { min-width: 0; }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; word-break: break-word; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.filter-input { min-width: 220px; height: 36px; flex: 1; position: relative; }
.filter-input .el-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--jf-color-text-secondary); }
.filter-input input, .filter-select { width: 100%; height: 36px; border: 1px solid var(--jf-color-border); border-radius: 8px; outline: none; background: #fff; color: var(--jf-color-text-primary); font-size: 14px; transition: border-color .2s, box-shadow .2s; }
.filter-input input { padding: 0 12px 0 36px; }
.filter-select { min-width: 124px; padding: 0 12px; }
.filter-input input:focus, .filter-select:focus { border-color: var(--jf-color-primary); box-shadow: 0 0 0 3px rgba(118, 183, 232, .14); }
.filter-bar__actions { display: flex; gap: 8px; }
.search-button, .reset-button { height: 36px; padding: 0 16px; border-radius: 8px; font-size: 14px; font-weight: 700; }
.search-button { background: var(--jf-color-primary); color: #fff; }
.reset-button { border: 1px solid var(--jf-color-border); background: #fff; color: var(--jf-color-text-primary); }
.card-grid { padding: 16px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.business-card { padding: 16px; border: 1px solid var(--jf-color-border); border-radius: 12px; transition: border-color .2s, box-shadow .2s; }
.business-card:hover { border-color: rgba(118, 183, 232, .55); box-shadow: 0 8px 22px rgba(37, 40, 43, .06); }
.business-card__head, .business-card__footer { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.business-card h2 { margin: 0; color: var(--jf-color-text-primary); font-size: 18px; }
.business-card small { display: block; margin-top: 6px; color: var(--jf-color-text-secondary); font-size: 12px; }
.business-card__body { margin: 16px 0; display: grid; gap: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.business-card__body p { margin: 0; }
.business-card__footer { padding-top: 14px; border-top: 1px solid var(--jf-color-border); align-items: center; color: var(--jf-color-text-secondary); font-size: 13px; }
.business-card__footer div { display: flex; gap: 12px; }
.business-card__footer button { background: transparent; color: var(--jf-color-primary-dark); font-size: 13px; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr { transition: background .2s; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-error { background: rgba(231, 101, 101, .12); color: var(--jf-color-error); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
@media (max-width: 1180px) {
  .summary-grid, .card-grid { grid-template-columns: 1fr; }
}
</style>
